// ─────────────────────────────────────────────────────────────────────────────
// approvals.js — client for the document sign-off gate.
//
// AI-drafted contractual instruments (site instructions, variations) are
// created server-side as `agentDraft: true, approvalStatus: "pending"`. They
// carry no authority — and must not be issued as PDFs — until a named,
// authorized engineer signs them off via the approveDocument Cloud Function.
// The server enforces the RBAC; this module is the UI's thin caller + the
// shared predicates that drive the badge and the PDF gate.
// ─────────────────────────────────────────────────────────────────────────────

// Resolve the Cloud Functions base URL. Prefer an explicit base; otherwise
// derive it from the AI proxy URL by stripping its trailing function name.
import { appCheckHeader } from "./appCheck";

export function functionsBase() {
  const explicit = (process.env.REACT_APP_FN_BASE_URL || "").trim();
  if (explicit) return explicit.replace(/\/+$/, "");
  const proxy = (process.env.REACT_APP_AI_PROXY_URL || "").trim();
  if (proxy) return proxy.replace(/\/[^/]*$/, "");
  return "";
}

// True only for an AI draft that still needs a human sign-off. Manually
// created records (no createdVia "agent", or already approved) are unaffected,
// so the gate never retroactively blocks existing hand-entered documents.
export function requiresSignoff(doc) {
  if (!doc) return false;
  const pending = String(doc.approvalStatus || "").toLowerCase() === "pending";
  const isAgent = doc.createdVia === "agent" || doc.agentDraft === true;
  return isAgent && pending;
}

// A document is issuable (PDF / official use) unless it is a pending AI draft.
export function isIssued(doc) {
  return !requiresSignoff(doc);
}

// Badge descriptor for a record's approval state. Returns null when there is
// nothing to show (a normal, manually-created record).
export function approvalBadge(doc) {
  if (!doc) return null;
  const status = String(doc.approvalStatus || "").toLowerCase();
  if (requiresSignoff(doc)) {
    return { label: "Pending sign-off", variant: "warning" };
  }
  if (status === "approved" && doc.approvedBy) {
    return { label: `Issued · ${doc.approvedBy}`, variant: "success" };
  }
  return null;
}

// Call the server to sign a document off. Resolves to the server payload, or
// throws an Error with a human-readable message (incl. 401/403/409 cases).
export async function approveDocument({ projectId, documentType, documentId }) {
  const base = functionsBase();
  if (!base) {
    throw new Error(
      "Sign-off endpoint not configured. Set REACT_APP_FN_BASE_URL (or REACT_APP_AI_PROXY_URL) in .env.local."
    );
  }

  let idToken = "";
  try {
    const { getAuth } = await import("firebase/auth");
    const user = getAuth().currentUser;
    if (user) idToken = await user.getIdToken();
  } catch (_) { /* fall through — server will reject without a token */ }
  if (!idToken) throw new Error("You must be signed in to sign off a document.");

  let res;
  try {
    res = await fetch(`${base}/approveDocument`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
        ...(await appCheckHeader()),
      },
      body: JSON.stringify({ projectId, documentType, documentId }),
    });
  } catch (networkError) {
    throw new Error(`Could not reach the sign-off service at ${base}.`);
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || `Sign-off failed (HTTP ${res.status}).`);
  }
  return data; // { success, documentType, documentId, approvalStatus, approvedBy }
}
